import { useEffect, useMemo, useState } from "react";
import { FiInfo, FiPercent, FiPlus, FiTrash2, FiX } from "react-icons/fi";

const GST_RATES = [0, 0.25, 1.5, 3, 5, 12, 18, 28];

const createBand = () => ({
  id: `${Date.now()}-${Math.random()}`,
  rate: "",
  purchaseMinimumItemValue: "",
  purchaseMaximumItemValue: "",
  saleMinimumItemValue: "",
  saleMaximumItemValue: "",
});

const initialForm = {
  displayName: "",
  ruleGroup: "APPAREL",
  description: "",
  isActive: true,
};

const FIXED_RULE_PRESETS = [
  { label: "Accessories 3%", rate: 3, group: "ACCESSORIES_3" },
  { label: "Accessories 5%", rate: 5, group: "ACCESSORIES_5" },
  { label: "Accessories 12%", rate: 12, group: "ACCESSORIES_12" },
  { label: "Accessories 18%", rate: 18, group: "ACCESSORIES_18" },
  { label: "Leather 18%", rate: 18, group: "LEATHER_18" },
];

const normalizeRuleGroup = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_ ]/g, "")
    .replace(/\s+/g, "_");

const asNumberOrNull = (value) =>
  value === "" || value === null || value === undefined ? null : Number(value);

const formatMoney = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const bandLabel = (minimumValue, maximumValue) => {
  const minimum = asNumberOrNull(minimumValue);
  const maximum = asNumberOrNull(maximumValue);

  if (minimum !== null && maximum !== null) {
    return `${formatMoney(minimum)} – ${formatMoney(maximum)}`;
  }

  if (minimum !== null) {
    return `${formatMoney(minimum)} and above`;
  }

  if (maximum !== null) {
    return `Up to ${formatMoney(maximum)}`;
  }

  return "All item values";
};

export default function GstSlabModal({
  open,
  onClose,
  onSave,
  slab = null,
  existingGroups = [],
}) {
  const [form, setForm] = useState(initialForm);
  const [bands, setBands] = useState([createBand()]);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setError("");

    if (slab) {
      setForm({
        displayName: slab.name || "",
        ruleGroup: slab.ruleGroup || "APPAREL",
        description: slab.description || "",
        isActive: slab.isActive !== false,
      });

      setBands([
        {
          id: slab.id,
          rate: slab.rate !== undefined ? String(slab.rate) : "",
          purchaseMinimumItemValue:
            slab.minimumItemValue === null ||
            slab.minimumItemValue === undefined
              ? ""
              : String(slab.minimumItemValue),
          purchaseMaximumItemValue:
            slab.maximumItemValue === null ||
            slab.maximumItemValue === undefined
              ? ""
              : String(slab.maximumItemValue),
          saleMinimumItemValue:
            slab.saleMinimumItemValue === null ||
            slab.saleMinimumItemValue === undefined
              ? ""
              : String(slab.saleMinimumItemValue),
          saleMaximumItemValue:
            slab.saleMaximumItemValue === null ||
            slab.saleMaximumItemValue === undefined
              ? ""
              : String(slab.saleMaximumItemValue),
        },
      ]);

      return;
    }

    setForm({
      ...initialForm,
      ruleGroup: "APPAREL",
    });

    setBands([createBand()]);
  }, [open, slab]);

  const normalizedRuleGroup = useMemo(
    () => normalizeRuleGroup(form.ruleGroup),
    [form.ruleGroup],
  );

  const groupSuggestions = useMemo(
    () =>
      [...new Set(existingGroups.map(normalizeRuleGroup).filter(Boolean))].sort(
        (first, second) => first.localeCompare(second),
      ),
    [existingGroups],
  );

  if (!open) return null;

  const updateForm = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const updateBand = (id, field, value) => {
    setBands((previous) =>
      previous.map((band) =>
        band.id === id ? { ...band, [field]: value } : band,
      ),
    );
  };

  const addBand = () => {
    setBands((previous) => [...previous, createBand()]);
  };

  const removeBand = (id) => {
    if (bands.length === 1) return;

    setBands((previous) => previous.filter((band) => band.id !== id));
  };

  const applyApparelExample = () => {
    setForm({
      displayName: "Apparel GST",
      ruleGroup: "APPAREL",
      description: "Automatic GST bands based on discounted item value.",
      isActive: true,
    });

    setBands([
      {
        id: `${Date.now()}-lower`,
        rate: "5",
        purchaseMinimumItemValue: "",
        purchaseMaximumItemValue: "2500",
        saleMinimumItemValue: "",
        saleMaximumItemValue: "2625",
      },
      {
        id: `${Date.now()}-higher`,
        rate: "18",
        purchaseMinimumItemValue: "2500.01",
        purchaseMaximumItemValue: "",
        saleMinimumItemValue: "2625.01",
        saleMaximumItemValue: "",
      },
    ]);
  };

  const applyFixedRulePreset = ({ label, rate, group }) => {
    setForm({
      displayName: `${label} GST`,
      ruleGroup: group,
      description: `Fixed ${rate}% GST rule for ${label.toLowerCase()}.`,
      isActive: true,
    });
    setBands([
      {
        id: `${Date.now()}-fixed-${rate}`,
        rate: String(rate),
        purchaseMinimumItemValue: "",
        purchaseMaximumItemValue: "",
        saleMinimumItemValue: "",
        saleMaximumItemValue: "",
      },
    ]);
  };

  const validateBands = () => {
    const normalizedBands = bands.map((band, index) => {
      const rate = Number(band.rate);
      const purchaseMinimumItemValue = asNumberOrNull(
        band.purchaseMinimumItemValue,
      );
      const purchaseMaximumItemValue = asNumberOrNull(
        band.purchaseMaximumItemValue,
      );
      const saleMinimumItemValue = asNumberOrNull(band.saleMinimumItemValue);
      const saleMaximumItemValue = asNumberOrNull(band.saleMaximumItemValue);

      if (!GST_RATES.includes(rate)) {
        throw new Error(`Select a valid GST rate for band ${index + 1}.`);
      }

      if (
        purchaseMinimumItemValue !== null &&
        (!Number.isFinite(purchaseMinimumItemValue) ||
          purchaseMinimumItemValue < 0)
      ) {
        throw new Error(`Enter a valid minimum value for band ${index + 1}.`);
      }

      if (
        purchaseMaximumItemValue !== null &&
        (!Number.isFinite(purchaseMaximumItemValue) ||
          purchaseMaximumItemValue < 0)
      ) {
        throw new Error(`Enter a valid maximum value for band ${index + 1}.`);
      }

      if (
        purchaseMinimumItemValue !== null &&
        purchaseMaximumItemValue !== null &&
        purchaseMinimumItemValue > purchaseMaximumItemValue
      ) {
        throw new Error(
          `Purchase minimum cannot exceed purchase maximum in band ${index + 1}.`,
        );
      }

      if (
        saleMinimumItemValue !== null &&
        (!Number.isFinite(saleMinimumItemValue) || saleMinimumItemValue < 0)
      ) {
        throw new Error(`Enter a valid sale minimum for band ${index + 1}.`);
      }

      if (
        saleMaximumItemValue !== null &&
        (!Number.isFinite(saleMaximumItemValue) || saleMaximumItemValue < 0)
      ) {
        throw new Error(`Enter a valid sale maximum for band ${index + 1}.`);
      }

      if (
        saleMinimumItemValue !== null &&
        saleMaximumItemValue !== null &&
        saleMinimumItemValue > saleMaximumItemValue
      ) {
        throw new Error(
          `Sale minimum cannot exceed sale maximum in band ${index + 1}.`,
        );
      }

      return {
        rate,
        purchaseMinimumItemValue,
        purchaseMaximumItemValue,
        saleMinimumItemValue,
        saleMaximumItemValue,
      };
    });

    for (let first = 0; first < normalizedBands.length; first += 1) {
      for (
        let second = first + 1;
        second < normalizedBands.length;
        second += 1
      ) {
        const firstBand = normalizedBands[first];
        const secondBand = normalizedBands[second];

        const overlaps = (minimumKey, maximumKey) => {
          const firstEndsBeforeSecond =
            firstBand[maximumKey] !== null &&
            secondBand[minimumKey] !== null &&
            firstBand[maximumKey] < secondBand[minimumKey];
          const firstStartsAfterSecond =
            firstBand[minimumKey] !== null &&
            secondBand[maximumKey] !== null &&
            firstBand[minimumKey] > secondBand[maximumKey];
          return !firstEndsBeforeSecond && !firstStartsAfterSecond;
        };

        if (
          overlaps("purchaseMinimumItemValue", "purchaseMaximumItemValue") ||
          overlaps("saleMinimumItemValue", "saleMaximumItemValue")
        ) {
          throw new Error(
            `Band ${first + 1} overlaps with band ${second + 1} in purchase or sale values.`,
          );
        }
      }
    }

    return normalizedBands;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.displayName.trim()) {
      setError("GST rule name is required.");
      return;
    }

    if (!normalizedRuleGroup) {
      setError("Rule group is required.");
      return;
    }

    try {
      const normalizedBands = validateBands();

      setError("");
      setIsSaving(true);

      if (slab) {
        const band = normalizedBands[0];

        await onSave({
          mode: "SINGLE",
          payload: {
            name: form.displayName.trim(),
            ruleGroup: normalizedRuleGroup,
            rate: band.rate,
            purchaseMinimumItemValue: band.purchaseMinimumItemValue,
            purchaseMaximumItemValue: band.purchaseMaximumItemValue,
            saleMinimumItemValue: band.saleMinimumItemValue,
            saleMaximumItemValue: band.saleMaximumItemValue,
            description: form.description.trim() || undefined,
            isActive: form.isActive,
          },
        });
      } else {
        await onSave({
          mode: "GROUP",
          payload: {
            displayName: form.displayName.trim(),
            ruleGroup: normalizedRuleGroup,
            description: form.description.trim() || undefined,
            isActive: form.isActive,
            bands: normalizedBands,
          },
        });
      }

      onClose();
    } catch (requestError) {
      setError(requestError.message || "Unable to save GST rules.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="mx-auto flex min-h-full max-w-4xl items-center py-6">
        <div className="w-full max-w-6xl overflow-hidden rounded-3xl bg-white shadow-2xl">
          <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5 sm:px-8 sm:py-6">
            <div>
              <div className="flex items-center gap-2 text-primary-700">
                <FiPercent />
                <span className="text-xs font-bold uppercase tracking-[0.14em]">
                  Tax catalogue
                </span>
              </div>

              <h2 className="mt-2 text-2xl font-bold text-gray-900">
                {slab ? "Edit GST Rule" : "Add GST Rule Group"}
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Add one or more GST price bands under the same product category.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="grid h-10 w-10 place-items-center rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
            >
              <FiX size={18} />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-6 p-6 sm:p-8">
              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                  {error}
                </div>
              )}

              {!slab && (
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={applyApparelExample}
                    className="flex w-full items-center gap-3 rounded-2xl border border-primary-100 bg-primary-50 p-4 text-left text-sm text-primary-900 transition hover:bg-primary-100"
                  >
                    <FiInfo className="shrink-0 text-primary-700" />
                    <span>
                      <b>Use Apparel price bands:</b> purchases at 5% up to
                      ₹2,500 before GST; sales at 5% up to ₹2,625 including GST.
                    </span>
                  </button>

                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <p className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500">
                      Fixed-rate quick rules
                    </p>
                    <p className="mt-1 text-sm text-gray-600">
                      Use a separate group for each fixed rate so 3%, 5%, 12%,
                      and 18% product categories never overlap.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {FIXED_RULE_PRESETS.map((preset) => (
                        <button
                          key={preset.group}
                          type="button"
                          onClick={() => applyFixedRulePreset(preset)}
                          className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800"
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    GST Rule Name *
                  </label>

                  <input
                    type="text"
                    placeholder="Apparel GST"
                    value={form.displayName}
                    onChange={(event) =>
                      updateForm("displayName", event.target.value)
                    }
                    className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Rule Group *
                  </label>

                  <input
                    list="gst-rule-group-list"
                    type="text"
                    placeholder="APPAREL"
                    value={form.ruleGroup}
                    onChange={(event) =>
                      updateForm("ruleGroup", event.target.value)
                    }
                    className="h-12 w-full rounded-xl border border-gray-200 px-4 font-mono uppercase outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                    required
                  />

                  <datalist id="gst-rule-group-list">
                    {groupSuggestions.map((group) => (
                      <option key={group} value={group} />
                    ))}
                    <option value="APPAREL" />
                    <option value="JEWELLERY" />
                    <option value="ACCESSORIES_3" />
                    <option value="ACCESSORIES_5" />
                    <option value="ACCESSORIES_12" />
                    <option value="ACCESSORIES_18" />
                    <option value="LEATHER_18" />
                    <option value="GENERAL" />
                  </datalist>
                </div>
              </div>

              <div className="overflow-hidden rounded-2xl border border-gray-200">
                <div className="flex flex-col gap-3 border-b border-gray-100 bg-gray-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="font-bold text-gray-900">GST rate bands</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Purchase values are before GST. Sale values are customer
                      prices including GST.
                    </p>
                  </div>

                  {!slab && (
                    <button
                      type="button"
                      onClick={addBand}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-800"
                    >
                      <FiPlus />
                      Add GST Band
                    </button>
                  )}
                </div>

                <div className="divide-y divide-gray-100">
                  {bands.map((band, index) => (
                    <div key={band.id} className="p-5">
                      <div className="grid gap-4 lg:grid-cols-[52px_180px_1fr_42px]">
                      <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary-50 font-bold text-primary-700">
                        {index + 1}
                      </div>

                        <div>
                        <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-500">
                          GST Rate *
                        </label>

                        <select
                          value={band.rate}
                          onChange={(event) =>
                            updateBand(band.id, "rate", event.target.value)
                          }
                          className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 outline-none focus:border-primary-700"
                          required
                        >
                          <option value="">Select rate</option>
                          {GST_RATES.map((rate) => (
                            <option key={rate} value={rate}>
                              {rate}%
                            </option>
                          ))}
                        </select>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2">
                          <ValueRangeFields
                            title="Purchase band"
                            description="Cost before GST"
                            minimumValue={band.purchaseMinimumItemValue}
                            maximumValue={band.purchaseMaximumItemValue}
                            onMinimumChange={(value) =>
                              updateBand(band.id, "purchaseMinimumItemValue", value)
                            }
                            onMaximumChange={(value) =>
                              updateBand(band.id, "purchaseMaximumItemValue", value)
                            }
                          />
                          <ValueRangeFields
                            title="Sale band"
                            description="Customer price including GST"
                            minimumValue={band.saleMinimumItemValue}
                            maximumValue={band.saleMaximumItemValue}
                            onMinimumChange={(value) =>
                              updateBand(band.id, "saleMinimumItemValue", value)
                            }
                            onMaximumChange={(value) =>
                              updateBand(band.id, "saleMaximumItemValue", value)
                            }
                          />
                        </div>

                        {!slab && (
                        <button
                          type="button"
                          onClick={() => removeBand(band.id)}
                          disabled={bands.length === 1}
                          className="mt-6 grid h-11 w-11 place-items-center rounded-xl border border-red-100 text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-30"
                          title="Remove GST band"
                        >
                          <FiTrash2 />
                        </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-900">
                Purchase bands cannot overlap with purchase bands; sale bands
                cannot overlap with sale bands. Use price bands for Apparel.
                For a product-specific rate, create one open-ended band in its
                own group (for example <b>ACCESSORIES_3</b> or <b>LEATHER_18</b>).
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  Description
                </label>

                <textarea
                  rows="3"
                  placeholder="Optional note for this GST rule group..."
                  value={form.description}
                  onChange={(event) =>
                    updateForm("description", event.target.value)
                  }
                  className="w-full resize-none rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  Status
                </label>

                <select
                  value={form.isActive ? "ACTIVE" : "INACTIVE"}
                  onChange={(event) =>
                    updateForm("isActive", event.target.value === "ACTIVE")
                  }
                  className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 outline-none focus:border-primary-700"
                >
                  <option value="ACTIVE">Active — available for billing</option>
                  <option value="INACTIVE">
                    Inactive — retained for history
                  </option>
                </select>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-gray-100 px-6 py-5 sm:flex-row sm:justify-end sm:px-8">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="h-11 rounded-xl border border-gray-200 px-6 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isSaving}
                className="h-11 rounded-xl bg-primary-700 px-6 font-semibold text-white hover:bg-primary-800 disabled:opacity-60"
              >
                {isSaving
                  ? "Saving..."
                  : slab
                    ? "Update GST Rule"
                    : `Save ${bands.length} GST Band${bands.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function ValueRangeFields({
  title,
  description,
  minimumValue,
  maximumValue,
  onMinimumChange,
  onMaximumChange,
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-3">
      <div className="mb-3">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-700">
          {title}
        </p>
        <p className="mt-0.5 text-xs text-gray-500">{description}</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs font-medium text-gray-500">
          Minimum
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="No min"
            value={minimumValue}
            onChange={(event) => onMinimumChange(event.target.value)}
            className="mt-1 h-10 w-full rounded-lg border border-gray-200 bg-white px-2.5 text-sm outline-none focus:border-primary-700"
          />
        </label>
        <label className="text-xs font-medium text-gray-500">
          Maximum
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="No max"
            value={maximumValue}
            onChange={(event) => onMaximumChange(event.target.value)}
            className="mt-1 h-10 w-full rounded-lg border border-gray-200 bg-white px-2.5 text-sm outline-none focus:border-primary-700"
          />
        </label>
      </div>

      <p className="mt-2 text-xs text-gray-400">
        {bandLabel(minimumValue, maximumValue)}
      </p>
    </div>
  );
}
